"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { motion } from "framer-motion";

export default function PricingPreviewSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    ref?.current?.querySelectorAll(".reveal")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="py-32 bg-[#11161D] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Pricing Card */}
          <div className="reveal">
            <div className="pricing-card-featured rounded-2xl p-10 bg-[#0B0F14]">
              <div className="tag-badge mb-6">Standard Tier</div>
              <div className="flex items-end gap-3 mb-2">
                <span
                  className="font-mono font-bold text-[#00E676]"
                  style={{ fontSize: "4rem", lineHeight: 1, textShadow: "0 0 30px rgba(0,230,118,0.4)" }}
                >
                  4%
                </span>
                <span className="text-[#6B7A8D] text-sm mb-3">per top-up</span>
              </div>
              <div className="divider-glow my-6" />
              <div className="space-y-3 mb-8">
                {[
                  { label: "Monthly Fee", val: "None" },
                  { label: "Setup Costs", val: "None" },
                  { label: "Lock-In Contracts", val: "None" },
                  { label: "Spending Limits", val: "None" },
                ]?.map((row) => (
                  <div key={row?.label} className="flex justify-between items-center">
                    <span className="text-[#6B7A8D] text-sm">{row?.label}</span>
                    <span className="text-[#00E676] font-mono text-sm font-bold">{row?.val}</span>
                  </div>
                ))}
              </div>
              <a
                href="https://wa.me/447700000000"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-solid-glow w-full py-4 rounded-xl text-sm font-bold uppercase tracking-[0.1em] block text-center"
              >
                Start On WhatsApp →
              </a>
            </div>
          </div>

          {/* White Label Preview */}
          <div className="reveal reveal-delay-2">
            <div className="tag-badge mb-6">Agency Tier</div>
            <h2 className="section-title text-[#F0F4F8] mb-6">
              Infrastructure For Agencies{" "}
              <span className="text-[#00E676]">Managing Volume.</span>
            </h2>
            <div className="space-y-3 mb-8">
              {[
                "Dedicated account manager",
                "Private priority support",
                "Volume-based rate adjustments",
                "KPI-aligned pricing",
                "Zero Scaled-Out branding",
              ]?.map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] flex-shrink-0" />
                  <span className="text-[#6B7A8D] text-sm">{item}</span>
                </div>
              ))}
            </div>
            <Link
              href="/agency"
              className="btn-glow px-8 py-4 rounded-full text-sm font-bold uppercase tracking-[0.1em] inline-block"
            >
              Become a Partner →
            </Link>
          </div>
        </div>
      </div>
    </motion.section>
  );
}